import { useIsMutating } from '@tanstack/react-query';
import { useContext } from 'react';

import { Button } from '@/components/ui/button';

import CourseEditContext from '../CourseEditContext';

const SAVE_DRAFT_PENDING_HINT = 'Unavailable while awaiting approval.';
const SAVE_DRAFT_PENDING_TOOLTIP =
	'Save draft is unavailable while this course is awaiting approval. You can save a draft again after it has been approved or changes have been requested.';

interface CourseModalSubmitProps {
	submitEvent(): void;
	draftSubmitEvent(): void;
}

const CourseEditSubmit = ({ submitEvent, draftSubmitEvent }: CourseModalSubmitProps) => {
	const isMutatingPosts = useIsMutating({ mutationKey: ['save-course-draft'] });
	const { isPendingApproval } = useContext(CourseEditContext);

	const isPending = isPendingApproval();
	const isDraftDisabled = isMutatingPosts !== 0 || isPending;
	const draftDisabledByApproval = isDraftDisabled && isPending;

	return (
		<div className="flex items-end gap-4 w-full justify-end">
			<div className="flex items-end gap-0.5">
				{draftDisabledByApproval && (
					<span className="text-[12px] text-grey-500 my-auto mr-2" id="save-draft-disabled-reason">
						{SAVE_DRAFT_PENDING_HINT}
					</span>
				)}
				<Button
					type="submit"
					variant="outline"
					disabled={isDraftDisabled}
					isLoading={isMutatingPosts !== 0}
					onClick={draftSubmitEvent}
					className="mr-0"
					title={
						isMutatingPosts !== 0
							? 'Saving draft…'
							: isPending
								? SAVE_DRAFT_PENDING_TOOLTIP
								: undefined
					}
					aria-label={
						isPending
							? 'Save draft. Unavailable while this course is awaiting approval.'
							: undefined
					}
					aria-describedby={draftDisabledByApproval ? 'save-draft-disabled-reason' : undefined}
				>
					Save Draft
				</Button>
			</div>
			<Button
				type="submit"
				disabled={isMutatingPosts !== 0}
				onClick={submitEvent}
				className="save-btn"
			>
				Submit for Review
			</Button>
		</div>
	);
};

export default CourseEditSubmit;
